import React, { FC } from 'react';

import { useIntl } from 'react-intl';

import { useUIContext } from 'state/ui';
import { useCreateOrderAriaLabel } from 'utils/accessibility/aria';
import { CartEntryType } from 'utils/cart';

import { RecentOrderCardWithModifiersView } from './recent-order-card-with-modifiers.view';
import { IRecentOrderCardWithModifiersProps } from './types';

/**
 *
 * RecentOrderCardWithModifiers is an alternative version of
 * the RecentOrderCard that displays extra information including
 * the item's modifiers. This is particularly useful for items
 * such as Coffee. Without modifiers visible, its hard to know much
 * about the coffee order.
 *
 */
const RecentOrderCardWithModifiers: FC<IRecentOrderCardWithModifiersProps> = ({
  productImage,
  backgroundImage,
  item,
  handleAddClick,
  isAvailable,
}) => {
  const { formatMessage } = useIntl();
  const modifiers = item.children ?? [];

  const firstTwoModifiers = modifiers.slice(0, 2).map(modifier => {
    let { name } = modifier;
    if (modifier.type === CartEntryType.itemOption && modifier.children?.[0]) {
      name = modifier.children[0].name || name;
    }
    return name ?? '';
  });
  const amountMore = modifiers.length - 2;
  const moreText = formatMessage({ id: 'amountMore' }, { amountMore });
  const moreModifiers = amountMore > 0 ? [moreText] : [];
  const addButtonText = formatMessage({ id: 'addToCart' });
  const unavailableMessage = formatMessage({ id: 'notAvailableAtStore' });
  const { buildImageUrl } = useUIContext();

  const createAriaLabel = useCreateOrderAriaLabel();

  const modifierProps = [...firstTwoModifiers, ...moreModifiers];
  return (
    <RecentOrderCardWithModifiersView
      name={item.name ?? ''}
      isAvailable={isAvailable && !item.isDisabled}
      productImage={productImage}
      backgroundImage={backgroundImage}
      handleAddClick={handleAddClick}
      unavailableMessage={unavailableMessage}
      addButtonText={addButtonText}
      modifiers={modifierProps}
      buildImageUrl={buildImageUrl}
      ariaLabel={createAriaLabel({
        buttonType: 'add',
        name: item.name,
        modifiers: modifierProps,
      })}
    />
  );
};

export default RecentOrderCardWithModifiers;
